import React from 'react';
import { connect } from 'react-redux';
import { Form, Button, Card } from 'react-bootstrap';
import { PageHeader, Loading } from '../../components';
import useComponentState from '../../utils/useComponentState';
import styles from './addTiers.module.scss';
import { addTier } from '../../api/tier';
import { toast } from 'react-toastify';
import {
    INITIAL_STATE,
    TOAST_MESSAGES,
    VALIDATION_ERROR_MESSAGES,
} from './addTiersConstants';

const validateAllFields = (state) => {
    const errors = {};

    if (!state.name || state.name.trim().length === 0) {
        errors.name = VALIDATION_ERROR_MESSAGES.name;
    }

    const minPts = parseFloat(state.minPoints);
    if (state.minPoints === '' || isNaN(minPts) || minPts < 1) {
        errors.minPoints = VALIDATION_ERROR_MESSAGES.minPoints;
    }

    const ratio = parseFloat(state.redemptionRatio);
    if (state.redemptionRatio === '' || isNaN(ratio) || ratio <= 0) {
        errors.redemptionRatio = VALIDATION_ERROR_MESSAGES.redemptionRatio;
    }

    return { isValid: Object.keys(errors).length === 0, errors };
};

const createPayload = (state) => ({
    name: state.name.trim(),
    minPoint: parseFloat(state.minPoints),
    description: state.description ? state.description.trim() : '',
    redemptionRatio: parseFloat(state.redemptionRatio),
});

const AddTiers = ({ auth }) => {
    const [state, setState] = useComponentState(INITIAL_STATE);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setState((prev) => ({
            ...prev,
            [name]: value,
            fieldErrors: { ...prev.fieldErrors, [name]: '' },
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const { isValid, errors } = validateAllFields(state);
        if (!isValid) {
            setState((prev) => ({ ...prev, fieldErrors: errors }));
            return;
        }

        setState((prev) => ({ ...prev, loading: true, error: '' }));

        try {
            const payload = createPayload(state);
            await addTier(auth.auth.token, payload);


            toast.success(TOAST_MESSAGES.success);
            setState(INITIAL_STATE);
        } catch (error) {
            const errorMessage = error.response?.data?.message || TOAST_MESSAGES.error;
            setState((prev) => ({ ...prev, loading: false, error: errorMessage }));
            toast.error(errorMessage);
        }
    };

    const handleCancel = () => setState(INITIAL_STATE);

    return (
        <div className={styles.root_div}>
            <PageHeader text='Add Tier' />

            <Card className={styles.formCard}>
                <Card.Body>
                    <Form noValidate onSubmit={handleSubmit}>
                        <div className='mb-4'>
                            <small className='text-muted'>
                                Fields marked with <span className='text-danger'>*</span> are required
                            </small>
                        </div>

                        <div className={styles.sectionContainer}>
                            <div className={styles.sectionTitle}>Tier Details</div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Tier Name <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='text'
                                    name='name'
                                    value={state.name}
                                    onChange={handleInputChange}
                                    placeholder='e.g. Bronze, Silver, Gold'
                                    isInvalid={!!state.fieldErrors?.name}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.name}
                                </Form.Control.Feedback>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Minimum Redemption Points <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='number'
                                    name='minPoints'
                                    value={state.minPoints}
                                    onChange={handleInputChange}
                                    placeholder='e.g. 0'
                                    step='any'
                                    min='1'
                                    isInvalid={!!state.fieldErrors?.minPoints}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.minPoints}
                                </Form.Control.Feedback>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>
                                    Redemption Ratio <span className='text-danger'>*</span>
                                </label>
                                <Form.Control
                                    type='number'
                                    name='redemptionRatio'
                                    value={state.redemptionRatio}
                                    onChange={handleInputChange}
                                    placeholder='e.g. 1.0'
                                    step='any'
                                    min='0.01'
                                    isInvalid={!!state.fieldErrors?.redemptionRatio}
                                />
                                <Form.Control.Feedback type='invalid' style={{ display: 'block' }}>
                                    {state.fieldErrors?.redemptionRatio}
                                </Form.Control.Feedback>
                            </div>

                            <div className={styles.fieldGroup}>
                                <label className={styles.fieldLabel}>Description</label>
                                <Form.Control
                                    as='textarea'
                                    name='description'
                                    value={state.description}
                                    onChange={handleInputChange}
                                    placeholder='Brief description of this tier'
                                    rows={2}
                                />
                            </div>
                        </div>

                        <div className={styles.formActions}>
                            <Button variant='secondary' type='button' onClick={handleCancel}>
                                Cancel
                            </Button>
                            <Button type='submit' variant='primary' disabled={state.loading}>
                                {state.loading ? 'Adding...' : 'Add Tier'}
                            </Button>
                        </div>
                    </Form>

                    {state.loading && <Loading />}
                </Card.Body>
            </Card>
        </div>
    );
};

const mapStateToProps = (state) => ({ auth: state.auth });

export default connect(mapStateToProps)(AddTiers);
